/**
 * MV8 (2026-10-08, R-008 `hidden-creature` / hidden mover): the page adapter of the stealth v2 walk hook. movement-truth.ts asks it, for an
 * admitted plain walk, whether the walk's feet meet any awareness pair; it runs janus-engine/movement/stealth-walk.ts on the walk's legacy feet
 * with: the scene's pair facts (index.ts campaignStealthPairFacts — distance, 9-ray cover, senses with the mover at the foot), the round's
 * contest cache (options/api/stealth_contests_inc.php, prefetched; first-writer-wins rows), and the dice — skill_roll_arr's twin
 * (skillRollArr: Sneak by the subject, then Attention by the observer at the light-driven DIS, both secret, one pair roll id, CMRT
 * suppressed as PHP's fire_caster_roll_type_triggers=false). Four extreme-dice preflights prove the write shape before the live draw (the
 * Overrun pattern); unsupported writes refuse the walk before any draw. Hydration runs before this synchronous verdict.
 */
import { planStealthWalk, stealthRoundKey, type StealthContest, type StealthPairFacts, type StealthWalkEvidence } from '../janus-engine/movement/stealth-walk.js';
import { stealthCompositionReason } from '../janus-engine/movement/stealth-walk-compose.js';
import { stealthWalkEffects } from '../janus-engine/movement/stealth-walk-effects.js';
import { decisionWrites, movementDecisionView, finishMovementDecision } from '../janus-engine/movement/decision-view.js';
import { HandBack, Refusal, ecChanges, ecLean, upper, type JanusCtx } from '../janus-engine/ability/context.js';
import { selectSkillForContestingRoll } from '../janus-engine/ability/select-skill-for-contesting-roll.js';
import { skillRollArr, CONTEST_ROLL_ID } from '../janus-engine/ability/skill-roll.js';
import { payloadDryRunRollSource } from '../janus-engine/ability/environmental-payload.js';
import { recordingRollSource, type RollSource } from '../janus-engine/actions/roll-source.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { teamUuid } from '../janus-engine/reactions/php-row-rules.js';
import { ftCampaignNowSeconds, ftClockCalendarFromArr, ftClockColsFromArr } from '../shared/campaign-clock.js';
import { abilityTruthReaders, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { actorEncounterFor, MovementRefused, registerStealthWalkPlanner, type StealthWalkPlanner } from './movement-truth.js';
import { createTriggerConsent, offerFortunate } from './trigger-consent.js';

type Row = Record<string, unknown>;
const row = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const truthy = (v: unknown): boolean => { const s = scalar(v); return s !== undefined && s !== null && s !== '' && s !== 0 && s !== '0' && s !== false; };
const SNEAK = '64493E62-BCAF-27F1-2B6A-EC84DE328F66';
const ATTENTION = 'F99172CC-DDEF-48D4-87F8-8B94F0071247';
/** FT_STEALTH_UNAWARE_OBSERVER_STATUSES */
const UNAWARE = ['unconscious', 'sleeping', 'paralyzed', 'petrified'];
const ROW_TYPES = new Set([CONTEST_ROLL_ID, 'unlucky_roll', 'gained_ascension_skill_point', 'failed_roll_for_ascension_skill_point', 'ascension_skill_tier_up',
  'no_roll', 'reaction_ability_applied']);
const ecOf = (r: Row | null | undefined): Row => row(r?.encounter_creatures) ?? {};
const idOf = (r: Row | null | undefined): string => upper(ecOf(r).encounter_creature_id);

// ── the round's contest cache (per encounter, one round key at a time) ──
interface HeldCache { key: string; contests: Map<string, { attention: number; sneak: number; adv_dis: number; roll_id: string }> }
const held = new Map<string, HeldCache>();
const flights = new Map<string, Promise<void>>();
/** Codex MV8 r2a P2: bumped by invalidate() — a response fetched under an older generation never reinstalls its snapshot. */
const generation = new Map<string, number>();
const offered = new Set<string>();
const genOf = (encounterId: string): number => generation.get(upper(encounterId)) ?? 0;
const pairKey = (o: string, s: string): string => `${upper(o)}|${upper(s)}`;
/** Diagnostics a smoke reads (the last plan's reason / key drift). */
const diag: { lastReason?: string; drift?: { page: string; server: string } } = {};
(window as unknown as { __ftStealthWalkDiag?: unknown }).__ftStealthWalkDiag = diag;
function fetchCache(encounterId: string, actorId: string, key: string): Promise<void> {
  const gen = genOf(encounterId);
  const flightKey = `${upper(encounterId)}|${upper(actorId)}|${key}|${gen}`;
  let p = flights.get(flightKey);
  if (p) return p;
  p = (async () => {
    const response = await fetch('/options/api/stealth_contests_inc.php', { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ encounter_id: upper(encounterId), actor_id: upper(actorId), round_key: key }) });
    const body = row(await response.json().catch(() => null));
    if (!response.ok || !body || !Array.isArray(body.contests)) return;
    if (genOf(encounterId) !== gen) return;
    if (String(body.round_key ?? '') !== key) { diag.drift = { page: key, server: String(body.round_key ?? '') }; return; }
    const contests = new Map<string, { attention: number; sneak: number; adv_dis: number; roll_id: string }>();
    for (const c of body.contests) {
      const r = row(c);
      if (r) contests.set(pairKey(String(r.observer_id), String(r.subject_id)), { attention: Number(r.attention), sneak: Number(r.sneak), adv_dis: Number(r.adv_dis), roll_id: upper(r.roll_id) });
    }
    held.set(`${upper(encounterId)}|${upper(actorId)}`, { key, contests });
  })().catch(() => undefined).finally(() => flights.delete(flightKey));
  flights.set(flightKey, p);
  return p;
}
function campaignFor(encounter: Row): Row | null {
  const id = upper(ecEnc(encounter).encounter_master_parent_id ?? encounter.encounter_master_parent_id);
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  const campaignId = (c: Row | null): string => upper(row(c?.campaigns)?.campaign_id ?? c?.campaign_id);
  return [row(id ? ft?.campaignCache?.get(id) : null), row(ft?.currentCampaignArr)].find(c => c && id && campaignId(c) === id) ?? null;
}
const ecEnc = (encounter: Row): Row => row(encounter.encounters) ?? encounter;
/** ft_stealth_round_key() on the page's rows: the encounter's in_combat / current_round, else the campaign's PERSISTED clock columns. */
export function pageStealthRoundKey(encounterId: string, encounter: Row): string | null {
  const e = ecEnc(encounter);
  const inCombat = Number(scalar(e.in_combat) ?? 0) === 1;
  if (inCombat) return stealthRoundKey(upper(encounterId), true, Number(scalar(e.current_round) ?? 0), null);
  const campaign = campaignFor(encounter);
  if (!campaign) return null;
  return stealthRoundKey(upper(encounterId), false, 0, ftCampaignNowSeconds(ftClockColsFromArr(campaign), ftClockCalendarFromArr(campaign)));
}
function definitionHeld(encounter: Row, id: string): boolean {
  // MV8 smoke T1–T5: the encounter catalog can hold a name/type SHELL of a skill (no items) — any FULL copy counts (ability-truth.ts definition())
  return [row(encounter.catalog)?.[id], window.FateTide?.objectCache?.get(id)].some(d => !!row(d) && !!row(row(d)!.items));
}

const planner: StealthWalkPlanner = {
  relevant(encounter, actorId, read) {
    const rows = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr.map(row).filter((r): r is Row => !!r) : [];
    return rows.some(r => !truthy(ecOf(r).encounter_creature_private) && safeHas(read, r, 'hidden') !== false) // a hidden row, or an unreadable one
      || rows.some(r => idOf(r) === upper(actorId) && safeHas(read, r, 'hidden') !== false);
  },
  hydrate(encounterId, actorId) {
    const encounter = actorEncounterFor(encounterId) as Row | null;
    if (!encounter) return null;
    const key = pageStealthRoundKey(encounterId, encounter);
    if (!key) return null; // The verdict names missing encounter/campaign/round inputs.
    const loading: Promise<unknown>[] = [SNEAK, ATTENTION].filter(id => !definitionHeld(encounter, id))
      .map(id => fetchAndCacheObject(id).catch(() => null));
    const roster = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []).map(row).filter((r): r is Row => !!r);
    if (held.get(`${upper(encounterId)}|${upper(actorId)}`)?.key !== key)
      loading.push(fetchCache(encounterId, actorId, key));
    const mover = roster.find(r => idOf(r) === upper(actorId));
    return mover ? Promise.all(loading).then(() => hydrateTurnTriggerDefinitions(mover, encounter, [], roster)).then(() => undefined)
      : loading.length ? Promise.all(loading).then(() => undefined) : null;
  },
  plan({ encounterId, actorId, feet, z, pair, read, consequences, preflightTrap, preflightHalt }) {
    let liveRoll: ReturnType<typeof recordingRollSource> | undefined;
    const back = (reason: string) => {
      diag.lastReason = reason;
      if (liveRoll?.draws.length) throw new MovementRefused('Movement refused after the stealth check. Nothing was moved.');
      return { kind: 'handback' as const, reason };
    };
    const encounter = actorEncounterFor(encounterId) as Row | null;
    if (!encounter) return back('stealth:encounter-not-held');
    const key = pageStealthRoundKey(encounterId, encounter);
    if (key === null) return back('stealth:campaign-not-held');
    if (key === '') return back('stealth:no-round-key');
    const cache = held.get(`${upper(encounterId)}|${upper(actorId)}`);
    if (!cache || cache.key !== key) return back(diag.drift?.page === key ? 'stealth:round-key-drift' : 'stealth:cache-not-held');
    if (![SNEAK, ATTENTION].every(id => definitionHeld(encounter, id))) return back('stealth:skill-definition-not-held');
    const members = new Map<string, Row>();
    const rosterIds: string[] = [];
    for (const raw of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
      const r = row(raw), id = idOf(r);
      if (r && id) { members.set(id, r); rosterIds.push(id); }
    }
    const mover = members.get(upper(actorId));
    if (!mover) return back('stealth:mover-not-held');
    const campaign = campaignFor(encounter);
    const campaignSeconds = campaign ? ftCampaignNowSeconds(ftClockColsFromArr(campaign), ftClockCalendarFromArr(campaign)) : null;
    // Codex MV8 r2a P2: the recorder's CAS revisions, proven for EVERY possible party before any die (never a hand-back after a live draw)
    const revAll = new Map<string, number>();
    for (const [id, m] of members) {
      const rev = Number(ecLean(m, 'movement_rev') ?? 0);
      if (!Number.isSafeInteger(rev) || rev < 0) return back('stealth:party-rev');
      revAll.set(id, rev);
    }
    // facts are dice-independent: one scene read per (observer, subject, foot) across the preflights and the live run
    const facts = new Map<string, StealthPairFacts | null>();
    const factsAt = (o: string, s: string, at: { x: number; y: number; z: number }): StealthPairFacts | null => {
      const k = `${o}|${s}|${at.x}|${at.y}|${at.z}`;
      if (!facts.has(k)) { let f: StealthPairFacts | null; try { f = pair(o, s, upper(actorId), at); } catch { f = null; } facts.set(k, f); }
      return facts.get(k)!;
    };
    // Codex Round 32 P2-2: `sweep` = a dice-independent readiness pass — every contest reads as UNSPOTTED, so the planner walks EVERY foot × roster
    // pair (when sneak and attention draw the same face class, all four dice patterns spot at pair 1 and later pairs / feet were never exercised:
    // real dice could reach a pair with null facts or a cold compile and hand back AFTER secret dice). Dry only; never recorded.
    const run = (roll: RollSource, dry: boolean, sweep = false): { kind: 'handback'; reason: string } | { kind: 'ok'; ctx: JanusCtx; plan: ReturnType<typeof planStealthWalk>;
      fresh: Map<string, { attention: number; sneak: number; adv_dis: number; roll_id: string; from: number; to: number }>; used: Set<string>;
      effects: NonNullable<StealthWalkEvidence['effects']> } => {
      const triggerConsent = createTriggerConsent(encounterId);
      const ctx: JanusCtx = { ...movementDecisionView(encounter, members),
        read: { ...abilityTruthReaders(mover, encounter, new Set(rosterIds)), triggerConsent }, roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [],
        inventoryChanged: [], statusConnectedId: null, rollPrivate: 2 };
      if (dry) ctx.dryRun = true;
      const fresh = new Map<string, { attention: number; sneak: number; adv_dis: number; roll_id: string; from: number; to: number }>();
      const used = new Set<string>();
      const skillRoll = (roller: Row, skillId: string, to: Row, adv: number): number => {
        const skill = selectSkillForContestingRoll(ctx, roller, [skillId]);
        skill.adv += adv;
        return skillRollArr(ctx, { casterId: idOf(roller), targetIds: [], abilityId: skillId, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
          useAbilityVariable1: null, useAbilityVariable2: null } as never, roller, skill, to, 0,
          { rollType: CONTEST_ROLL_ID, usedRollType: '', rollItemId: skillId, isTarget: false, fireCasterRollTypeTriggers: false });
      };
      let rollSeq = 0;
      const plan = planStealthWalk(feet, z, actorId, rosterIds, {
        row: id => ctx.members.get(upper(id)) ?? null,
        hidden: r => { const has = safeHas(read, r, 'hidden'); if (has === null) throw new HandBack('stealth:hidden-status-unreadable'); return has; },
        eligible: (o, s) => observerEligible(o, s, read),
        facts: factsAt,
        contest: (o, s, advDis): StealthContest | null => {
          const k = pairKey(o, s);
          const c = cache.contests.get(k);
          const unspotted = <T extends { sneak: number }>(v: T): T => sweep ? { ...v, sneak: Number.POSITIVE_INFINITY } : v;
          // MV10 r1: a CACHED pair's result is deterministic — the sweep keeps it (forcing it unspotted would explore feet no draw can reach)
          if (c) { used.add(k); return { ...c, fromCache: true }; }
          const f = fresh.get(k);
          if (f) return unspotted({ ...f, fromCache: true });
          const observer = ctx.members.get(upper(o)), subject = ctx.members.get(upper(s));
          if (!observer || !subject || !row(row(observer.computed)?.creature) || !row(subject.item_layer_types) || !row(observer.item_layer_types))
            throw new HandBack('stealth:party-compile-not-held');
          const from = ctx.rows.length;
          const sneak = skillRoll(subject, SNEAK, observer, 0);
          const attention = skillRoll(ctx.members.get(upper(o))!, ATTENTION, ctx.members.get(upper(s))!, advDis);
          const rollId = dry ? `00000000-0000-4000-8000-${String(++rollSeq).padStart(12, '0')}` : crypto.randomUUID().toUpperCase();
          fresh.set(k, { attention, sneak, adv_dis: advDis, roll_id: rollId, from, to: ctx.rows.length });
          return unspotted({ attention, sneak, adv_dis: advDis, roll_id: rollId, fromCache: false });
        },
      });
      if (plan.kind === 'handback') return plan;
      const contestRows = ctx.rows.length;
      const effects = stealthWalkEffects(ctx, plan, campaignSeconds);
      if (ctx.rows.length !== contestRows) return { kind: 'handback', reason: 'stealth:status-tail-log-not-modelled' };
      if (triggerConsent.pending().length) return { kind: 'handback', reason: 'stealth:trigger-consent-not-modelled' };
      if (ctx.chargesChanged.length || ctx.inventoryChanged.length || (ctx.statusStacks ?? []).length || (ctx.concentrationBreaks ?? []).length
        || (ctx.deathTail ?? []).length || (ctx.statusTriggerListeners ?? []).length) return { kind: 'handback', reason: 'stealth:unsupported-writes' };
      if (ctx.rows.some(r => !ROW_TYPES.has(r.roll_type_1))) return { kind: 'handback', reason: 'stealth:row-type' };
      for (const cols of Object.values(ecChanges(ctx))) if (Object.keys(cols).some(c => c !== 'luck' && c !== 'reaction')) return { kind: 'handback', reason: 'stealth:column-outside-allowlist' };
      const pcWrites = ctx.playerCharactersChanged ?? [];
      if (new Set(pcWrites.map(p => upper(p.item_id))).size !== pcWrites.length)
        return { kind: 'handback', reason: 'stealth:multiple-ascension-writes-not-modelled' };
      return { kind: 'ok', ctx, plan, fresh, used, effects };
    };
    try {
      let touched = false;
      let freshPossible = false, moverPcPossible = false;
      const haltSteps = new Set<number>();
      const trapSeeds: StealthWalkEvidence['verdict']['ec'][] = [];
      const preflight = (dry: Extract<ReturnType<typeof run>, { kind: 'ok' }>): void => {
        freshPossible ||= dry.fresh.size > 0;
        const moverPcId = upper(row(mover.player_characters)?.item_id);
        moverPcPossible ||= !!moverPcId && (dry.ctx.playerCharactersChanged ?? []).some(c => upper(c.item_id) === moverPcId);
        if (dry.plan.kind === 'resolved' && (dry.fresh.size || dry.plan.reveals.length)) touched = true;
        if (dry.plan.kind === 'resolved') for (const evaluation of dry.plan.evaluations) haltSteps.add(evaluation.step);
        trapSeeds.push(ecChanges(dry.ctx));
      };
      for (const pattern of ['max', 'min', 'alt-hi', 'alt-lo'] as const) {
        const dry = run(payloadDryRunRollSource(pattern), true);
        if (dry.kind === 'handback') return back(`preflight:${pattern}:${dry.reason}`);
        preflight(dry);
      }
      // every dice pattern again on the unspotted sweep: a later pair's luck / fortunate / ascension rows depend on its own faces (MV9 r1)
      for (const pattern of ['max', 'min', 'alt-hi', 'alt-lo'] as const) {
        const sweep = run(payloadDryRunRollSource(pattern), true, true);
        if (sweep.kind === 'handback') return back(`preflight:sweep-${pattern}:${sweep.reason}`);
        preflight(sweep);
      }
      // MV10 (2026-10-08): cached reveals compose with dice; fresh contests only with dice-free passes or a seeded, preflighted trap.
      const reason = stealthCompositionReason(consequences, touched, freshPossible, moverPcPossible);
      if (reason) return back(reason);
      // MV10 Round 34 P2: every evaluated foot in ANY dry/sweep run can halt; admit all cuts before the first live die.
      for (const step of haltSteps) {
        const why = preflightHalt?.(step, { touched, freshPossible, moverPcPossible });
        if (why) return back(why);
      }
      if (freshPossible && consequences.trapStep !== null) {
        if (!preflightTrap) return back('walk-trap:planner-not-registered');
        for (const seed of trapSeeds) { const why = preflightTrap(seed); if (why) return back(why); }
      }
      liveRoll = recordingRollSource(browserRollSource());
      const live = run(liveRoll, false);
      if (live.kind === 'handback') return back(live.reason);
      const { ctx, plan, fresh, used, effects } = live;
      if (plan.kind !== 'resolved') return back('stealth:unresolved');
      // nothing to record: no new contest and no spot (re-evaluating the round's cached dice writes nothing) — an ordinary walk
      if (!fresh.size && !plan.reveals.length) { diag.lastReason = used.size ? 'cached-clear' : 'clear'; return { kind: 'clear' as const }; }
      const parties = new Set<string>();
      for (const k of [...fresh.keys(), ...used]) for (const id of k.split('|')) parties.add(id);
      for (const r of plan.reveals) { parties.add(r.subject); parties.add(r.observer); }
      for (const s of [...effects.statusesAdded, ...effects.statusesRemoved]) parties.add(upper(s.target));
      for (const s of effects.statusesAdded) parties.add(upper(s.caster));
      for (const id of Object.keys(ecChanges(ctx))) parties.add(upper(id));
      parties.delete(upper(actorId));
      const revs: Record<string, number> = {};
      for (const id of parties) revs[id] = revAll.get(id)!;
      const ec: StealthWalkEvidence['verdict']['ec'] = {}, ecBefore: StealthWalkEvidence['verdict']['ecBefore'] = {};
      for (const [id, cols] of Object.entries(ecChanges(ctx))) for (const [col, value] of Object.entries(cols)) {
        const before = ecLean(members.get(upper(id))!, col);
        if (String(before ?? '') === String(value ?? '')) continue;
        (ec[upper(id)] ??= {})[col] = value; (ecBefore[upper(id)] ??= {})[col] = before ?? 0;
      }
      const evidence: StealthWalkEvidence = { round_key: cache.key, halt_step: plan.haltStep,
        contests: [...fresh].map(([k, f]) => {
          const [observer, subject] = k.split('|') as [string, string];
          return { observer, subject, roll_id: f.roll_id, attention: f.attention, sneak: f.sneak, adv_dis: f.adv_dis,
            rows: ctx.rows.slice(f.from, f.to).map(r => ({ ...r, private: (r.roll_type_1 === CONTEST_ROLL_ID ? 2 : 0) as 0 | 2 })) };
        }),
        cached: [...used].map(k => { const [observer, subject] = k.split('|') as [string, string]; return { observer, subject, roll_id: cache.contests.get(k)!.roll_id }; }),
        reveals: plan.reveals.map(r => ({ subject: r.subject, observer: r.observer })), revs,
        verdict: { ec, ecBefore, pc: ctx.playerCharactersChanged ?? [] }, effects,
        ...(ctx.fortunateOffers?.size ? { fortunateOffers: [...ctx.fortunateOffers] } : {}) };
      diag.lastReason = plan.haltStep === null ? 'contested' : 'halted';
      finishMovementDecision(ctx);
      return decisionWrites({ kind: 'resolved' as const, haltStep: plan.haltStep, message: plan.message, evidence });
    } catch (error) {
      if (liveRoll?.draws.length) throw new MovementRefused('Movement refused after the stealth check. Nothing was moved.');
      return back(error instanceof HandBack ? error.reason : error instanceof Refusal ? error.refusal : `stealth:exception:${String(error).slice(0, 120)}`);
    }
  },
  recorded(encounterId, evidence, actorId, operationId) {
    // every actor's cache of this encounter holding the round: each pair lands in the caches of the two creatures it names
    for (const [k, cache] of held) {
      if (!k.startsWith(upper(encounterId) + '|') || cache.key !== evidence.round_key) continue;
      const actor = k.slice(upper(encounterId).length + 1);
      for (const c of evidence.contests) if (upper(c.observer) === actor || upper(c.subject) === actor)
        cache.contests.set(pairKey(c.observer, c.subject), { attention: c.attention, sneak: c.sneak, adv_dis: c.adv_dis, roll_id: upper(c.roll_id) });
    }
    if (actorId && operationId && evidence.fortunateOffers?.length && !offered.has(operationId)) {
      offered.add(operationId);
      if (offered.size > 256) offered.delete(offered.values().next().value!);
      offerFortunate(encounterId, actorId, evidence.fortunateOffers);
    }
  },
  invalidate(encounterId) {
    for (const k of [...held.keys()]) if (k.startsWith(upper(encounterId) + '|')) held.delete(k);
    generation.set(upper(encounterId), genOf(encounterId) + 1);
  },
};
function safeHas(read: { hasStatus: (r: Row, k: string) => boolean }, r: Row, key: string): boolean | null {
  try { return read.hasStatus(r, key); } catch { return null; }
}
/** ft_stealth_observer_eligible(): a public, living, aware row on ANOTHER team (team ids compared as stored, as PHP does). */
function observerEligible(observer: Row, subject: Row, read: { hasStatus: (r: Row, k: string) => boolean; value: (r: Row, k: string) => unknown }): boolean {
  const o = idOf(observer), s = idOf(subject);
  if (!o || !s || o === s) return false;
  if (truthy(ecOf(observer).encounter_creature_dead)) return false;
  for (const key of UNAWARE) { const has = safeHas(read, observer, key); if (has === null) throw new HandBack('stealth:unaware-status-unreadable'); if (has) return false; }
  if (truthy(ecOf(observer).encounter_creature_private) || truthy(ecOf(subject).encounter_creature_private)) return false;
  const to = teamUuid(observer, read.value), ts = teamUuid(subject, read.value);
  if (!to || !ts) return false;
  return to !== ts;
}
registerStealthWalkPlanner(planner);
// keep the round's cache warm for any encounter holding a hidden creature
const warm = (encounter: Row | null): void => {
  if (!encounter) return;
  const id = upper(ecEnc(encounter).encounter_id ?? encounter.encounter_id);
  const rows = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr.map(row).filter((r): r is Row => !!r) : [];
  const hiddenId = upper((window as unknown as { VISUAL_STATUS_EFFECTS?: Record<string, string> }).VISUAL_STATUS_EFFECTS?.hidden);
  if (!id || !hiddenId || !JSON.stringify(rows).toUpperCase().includes(hiddenId)) return;
  const key = pageStealthRoundKey(id, encounter);
  // the endpoint's gate is the movement receivers' own: an actor this user may move
  const may = (window as unknown as { ftMovementPermission?: (e: string, a: string) => { allowed?: boolean } | null }).ftMovementPermission;
  // each actor this user may move (bounded): its own pairs of the round
  if (!key) return;
  for (const r of rows.filter(r => idOf(r) && may?.(id, idOf(r))?.allowed === true).slice(0, 12))
    void planner.hydrate?.(id, idOf(r));
};
// the overrun planner's sweep pattern: events may carry no encounterArr, and the encounter may be installed before this module loads
const sweep = (): void => { for (const e of window.FateTide?.encounterCache?.values() ?? []) { try { warm(row(e)); } catch { /* warm only */ } } };
for (const name of ['ft:encounter-state-received', 'ft:encounter-installed', 'ft:encounter-rows-patched'])
  window.addEventListener(name, e => { const enc = row((e as CustomEvent).detail?.encounterArr); if (enc) warm(enc); else sweep(); });
sweep();
setTimeout(sweep, 2000);
setTimeout(sweep, 8000);
